// src/components/resources/RelatedResourcesSection.tsx

import type { ReactNode } from 'react'
import type { ResourceInstance } from '~/lib/resources/types/shared/base'
import { RelatedResources } from '~/components/resources/RelatedResources'

/**
 * One titled group of related resources (e.g. "Related Calculators").
 * Empty groups are dropped by {@link RelatedResourcesSection}.
 */
export interface RelatedResourcesGroup {
  /** Section heading passed through to {@link RelatedResources}. */
  title: string
  /** The resources to render in this group. */
  resources: ResourceInstance[]
  /** Max items to show (defaults to 3, matching {@link RelatedResources}). */
  maxItems?: number
}

interface RelatedResourcesSectionProps {
  /** Ordered groups; each non-empty one renders a {@link RelatedResources} block. */
  groups: RelatedResourcesGroup[]
}

/**
 * Shared renderer for the "related resources" cluster in the tool detail
 * layouts. `CalculatorLayout`, `AnalyzerLayout`, and `GeneratorLayout` each
 * carried a byte-equivalent `RelatedContentView` — a null-gate plus one
 * `RelatedResources` block per non-empty resource kind inside
 * `mt-12 space-y-8`. This is the single source of truth for that markup; the
 * layouts now just map their (differently-shaped) precomputed bundles into an
 * ordered `groups` list.
 *
 * Renders nothing when every group is empty (so the caller never ships an empty
 * wrapper).
 *
 * @param props - {@link RelatedResourcesSectionProps}.
 */
export function RelatedResourcesSection({ groups }: RelatedResourcesSectionProps): ReactNode {
  const nonEmpty = groups.filter((group) => group.resources.length > 0)
  if (nonEmpty.length === 0) {
    return null
  }

  return (
    <div className="mt-12 space-y-8">
      {nonEmpty.map((group, index) => (
        <RelatedResources
          // Title-plus-index so two groups sharing a title can't collide on key.
          key={`${group.title}-${index}`}
          resources={group.resources}
          title={group.title}
          maxItems={group.maxItems ?? 3}
        />
      ))}
    </div>
  )
}
